import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';
import Navbar from './Navbar';
import formatDateTime from '../../components/formatDateTime';
import MainButton from '../../components/ul/MainButton';
import { FaUser } from 'react-icons/fa6';
import generateCaptcha from '../../components/captcha';
import { FaArrowLeft, FaArrowRight, FaSortAmountDownAlt, FaSortAmountUp } from 'react-icons/fa';
import { IoMdSearch } from 'react-icons/io';
import DeleteButton from '../../components/ul/DeleteButton';
import { MdDeleteSweep } from 'react-icons/md';
import NotFound from '../../components/ul/NotFound';
import getISTDate from '../../components/getISTDate';


const UserLogin = () => {
    const [loader, setLoader] = useState(false);
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [sortOrder, setSortOrder] = useState(-1);
    const [selectAll, setSelectAll] = useState(false);
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    const [totalUser, setTotalUser] = useState(0);
    const [selectState, setSelectState] = useState('today');
    const limit = 100; // Items per page
    // Fetch data from API
    const fetchData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getAllUserLogin(currentPage, startDate, endDate, limit, search, sortOrder);
            if (response?.status) {
                setData(response?.data);
                setTotalPages(response?.totalPage);
                setTotalUser(response?.totalRecord);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoader(false);
        }
    };
    // Fetch data from API
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [currentPage, sortOrder, startDate, endDate]);

    const handleSelectAll = () => {
        if (!selectAll) {
            const allUserIds = data.map((user) => user._id);
            setSelectedUsers(allUserIds);
        } else {
            setSelectedUsers([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectUser = (id) => {
        if (selectedUsers.includes(id)) {
            setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
        } else {
            setSelectedUsers([...selectedUsers, id]);
        }
    };

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        // Convert any UTC date to IST (UTC +5:30) and return YYYY-MM-DD
        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in ms
            return new Date(date.getTime() + istOffset).toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectState('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectState('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectState('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectState('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectState('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectState('thisYear');
                break;

            default:
                start = '';
                end = '';
        }

        setStartDate(start);
        setEndDate(end);
        setCurrentPage(1); // Reset page when filter changes
    };


    const handleDeleteSelected = async () => {
        const content = `You are about to delete a user login. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            try {
                const body = { id: selectedUsers };
                const response = await Calling.deleteUserLogin(body);
                if (response?.status) {
                    toast.success(response?.message);
                    setSelectedUsers([]);
                    setSelectAll(false); // Reset "Select All"
                    fetchData();
                } else {
                    toast.error(response?.message);
                }
            } catch (error) {
                console.error('Error deleting users:', error);
            } finally {
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    const handleUser = (id) => {
        navigate(`/manage/user/all/info/${id}`)
    }

    const handleReportSort = () => {
        setSelectState('sort')
        if (sortOrder === 1) {
            setSortOrder(-1)
        } else {
            setSortOrder(1)
        }
    }
    const handleSearchUser = (e) => {
        setCurrentPage(1);
        setSearch(e.target.value)
        setSelectState('search')
    }

    const handleFormDate = (e) => {
        if (e.target.name === "startDate") {
            setStartDate(e.target.value);
            setSelectState('range');
        } else {
            setEndDate(e.target.value);
            setSelectState('range');

        }
    }

    useEffect(() => {
        if (search.trim() !== '') {
            const delayDebounce = setTimeout(() => {
                fetchData();
            }, 500);

            return () => clearTimeout(delayDebounce);
        }
    }, [search]);

    return (
        <>

            <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
                <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">User Management</h1>
                <div className='bg-main-white rounded-xl'>
                    <Navbar selectState={'login'} />
                    <hr className='border-main-border ' />
                    <div className="p-4 bg-main-th mx-4 my-5 rounded-xl">
                        <h2 className="text-lg text-main-subfont">Total User Login</h2>
                        {loader ? (
                            <span className="flex items-center text-main-subfont">
                                <svg className="animate-spin h-5 w-5 mr-2 text-main-subfont" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Loading...
                            </span>
                        ) : (
                            <p className="text-xl text-main-font">{totalUser < 10 ? '0' + totalUser : totalUser}</p>
                        )}
                    </div>
                    <hr className='border-main-border border-[2px]' />

                    {/* Date Range Filters */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between w-full space-y-4 lg:space-y-0 lg:space-x-4 px-4 py-3">
                        {/* Buttons Container - Better Responsive Layout */}
                        <div className="flex flex-wrap gap-2 justify-start">
                            <MainButton
                                onClick={() => handleDateRangeFilter('today')}
                                label={'Today'}
                                selectState={selectState === 'today'}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('yesterday')}
                                label={'Yesterday'}
                                selectState={selectState === "yesterday"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisWeek')}
                                label={'This Week'}
                                selectState={selectState === 'thisWeek'}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisMonth')}
                                label={'This Month'}
                                selectState={selectState === 'thisMonth'}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('last6Months')}
                                label={'Last 6 Months'}
                                selectState={selectState === 'last6Months'}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisYear')}
                                label={'This Year'}
                                selectState={selectState === 'thisYear'}
                            />
                        </div>

                        {/* Date Input Container - Improved Responsive Layout */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 lg:w-auto lg:flex-nowrap lg:whitespace-nowrap">
                            <div className="flex-1 lg:w-auto">
                                <input
                                    type="date"
                                    value={startDate}
                                    name="startDate"
                                    onChange={handleFormDate}
                                    placeholder="Start Date"
                                    className={`border cursor-pointer bg-main-white w-full ${selectState === "range"
                                        ? 'border-main-blue text-main-blue'
                                        : 'border-main-border text-main-subfont'
                                        } px-4 py-2 rounded-lg focus:border-main-blue focus:outline-none shadow-sm`}
                                />
                            </div>
                            <div className="flex-1 lg:w-auto">
                                <input
                                    type="date"
                                    value={endDate}
                                    name="endDate"
                                    onChange={handleFormDate}
                                    placeholder="End Date"
                                    className={`border cursor-pointer bg-main-white w-full ${selectState === "range"
                                        ? 'border-main-blue text-main-blue'
                                        : 'border-main-border text-main-subfont'
                                        } px-4 py-2 rounded-lg focus:border-main-blue focus:outline-none shadow-sm`}
                                />
                            </div>
                        </div>
                    </div>

                    <hr className='bg-main-border/10 mx-4' />
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between w-full gap-4 px-4 py-3">
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2">
                            <div>
                                <MainButton
                                    onClick={handleReportSort}
                                    label={
                                        <p className='flex gap-2 items-center'>
                                            {sortOrder === -1 ? <FaSortAmountDownAlt /> : <FaSortAmountUp />}
                                            <span>Sort</span>
                                        </p>
                                    }
                                    selectState={selectState === 'sort'}
                                    className='col-span-1 w-26 hover:text-main-blue hover:border-main-blue'
                                />
                            </div>
                        </div>
                        <div className="relative w-full lg:w-64 xl:w-80 mt-2 lg:mt-0">
                            <IoMdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-main-subfont" size={18} />
                            <input
                                type="search"
                                className="p-2 pl-10 border border-main-border rounded-3xl focus:ring-2 focus:ring-main-subfont  focus:outline-none focus:border-transparent w-full"
                                value={search}
                                onChange={handleSearchUser}
                                placeholder="Search"
                            />
                        </div>
                    </div>

                    {/* Table of Users */}
                    <div className="overflow-x">
                        <table className='w-full my-5 text-left'>
                            <thead className='bg-main-th text-main-font uppercase'>
                                <tr className=' border-primary-dark'>
                                    <th className="ps-4 py-3 text-left text-xs font-medium rounded-tl-lg">
                                        <input
                                            type="checkbox"
                                            className='me-2'
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                        />
                                        {selectedUsers.length > 0 && (
                                            <DeleteButton
                                                onClick={() => handleDeleteSelected(null)} label={<MdDeleteSweep size={20} />} />
                                        )}
                                    </th>
                                    <th className="p-2 text-xs font-medium">#</th>
                                    <th className="p-2 text-xs font-medium">Email & deviceId</th>
                                    <th className="p-2 text-xs font-medium">Date</th>
                                    <th className="p-2 text-xs font-medium rounded-tr-lg">User</th>
                                </tr>
                            </thead>
                            {loader ? (
                                <tbody>
                                    <tr>
                                        <td colSpan={11} className="text-center py-4">
                                            <Loader loader={loader} />
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                <tbody>
                                    {data.length > 0 ? data.map((user, index) => (
                                        <tr key={user._id} className='hover:bg-main-thHover'>
                                            <td className="ps-4 text-sm font-medium">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(user._id)}
                                                    onChange={() => handleSelectUser(user._id)}
                                                />
                                            </td>
                                            <td className="p-2">{index + 1 + (currentPage - 1) * limit}</td>
                                            <td className="p-2">{user?.l_email || user?.l_device}</td>
                                            <td className="p-2">{formatDateTime(user.createdAt)}</td>
                                            <td className="p-2">
                                                <button
                                                    onClick={() => handleUser(user?.u_info)}
                                                    className="bg-main-blue text-main-white px-2 py-1 rounded hover:bg-main-blue/80 shadow-lg"
                                                >
                                                    <FaUser />
                                                </button>
                                            </td>
                                        </tr>
                                    )) : <tr><td className='' colSpan={6}><NotFound /></td></tr>}
                                </tbody>)}
                        </table>
                    </div>

                    {/* Pagination - Responsive Layout */}
                    <div className="flex flex-col md:flex-row justify-center items-center p-6 relative">
                        <div className="flex gap-6 mb-4 md:mb-0">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className={`px-2 w-[110px] flex items-center justify-center gap-2 py-2 rounded-lg ${currentPage === 1
                                    ? "bg-main-white border border-main-border text-main-subfont"
                                    : "bg-main-blue text-main-white"
                                    }`}
                            >
                                <FaArrowLeft /> Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className={`px-2 w-[110px] flex items-center justify-center gap-2 py-2 rounded-lg ${currentPage === totalPages
                                    ? "bg-main-white border border-main-border text-main-subfont"
                                    : "bg-main-blue text-main-white"
                                    }`}
                            >
                                Next <FaArrowRight />
                            </button>
                        </div>

                        <p className="text-main-subfont md:absolute md:right-6 mt-2 md:mt-0">
                            <span className="border border-main-border text-main-font px-2 py-1 rounded-lg">
                                {currentPage <= 10 ? "0" + currentPage : currentPage}
                            </span>{" "}
                            of {totalPages <= 10 ? "0" + totalPages : totalPages}
                        </p>
                    </div>

                </div>
            </div>

        </>
    );
}

export default UserLogin